import React from "react";
import { Select } from "antd";

export interface ClassOption {
  value: number;
  label: string;
}

interface ClassesSelectorProps {
  /** Grades the user is assigned to. With none, no class can be picked yet. */
  userGrades: number[];
  selectedClassIds: number[];
  options: ClassOption[];
  onChange: (classIds: number[]) => void;
}

/**
 * Multi-select for a school user's classes.
 *
 * Declared at MODULE scope on purpose. This used to be defined inside
 * UsersTab's body, which made every parent render produce a NEW component
 * type — so React unmounted and remounted the Select instead of updating it.
 * Picking a class calls onChange, which re-renders the parent, which
 * remounted this Select and closed its dropdown: the operator had to reopen
 * the list for every single class. A stable module-level identity keeps the
 * same Select instance (and its open state) alive across re-renders.
 */
const ClassesSelector: React.FC<ClassesSelectorProps> = ({
  userGrades,
  selectedClassIds,
  options,
  onChange,
}) => {
  if (userGrades.length === 0) {
    return (
      <Select
        mode="multiple"
        value={[]}
        placeholder="Seleccionar clases"
        style={{ width: "100%" }}
        disabled
        notFoundContent="Primero selecciona al menos un grado"
      />
    );
  }

  return (
    <Select
      mode="multiple"
      value={selectedClassIds}
      onChange={onChange}
      placeholder="Seleccionar clases"
      style={{ width: "100%" }}
      maxTagCount={3}
      maxTagTextLength={10}
      dropdownStyle={{ maxHeight: 300, overflowY: "auto" }}
      showSearch
      // Option values are numeric classroom ids and Select's default
      // optionFilterProp is `value`, so searching a class CODE would match
      // nothing without this.
      optionFilterProp="label"
      options={options}
      notFoundContent="No hay clases disponibles"
      allowClear
    />
  );
};

export default ClassesSelector;
